'use client'

import { cva } from 'class-variance-authority'
import { Dialog as DialogPrimitive } from 'radix-ui'
import React from 'react'
import type { VariantProps } from 'class-variance-authority'
import Close from '@/icons/Close'
import { cn, warnIfEmptyLabel } from '@/utils'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogPrimitiveClose = DialogPrimitive.Close

const dialogOverlayVariants = cva(
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50',
  {
    variants: {
      opacity: {
        none: 'bg-transparent',
        lightest: 'bg-black/10',
        lighter: 'bg-black/20',
        light: 'bg-black/33',
        medium: 'bg-black/50',
        dark: 'bg-black/66',
        darker: 'bg-black/75',
        darkest: 'bg-black/85',
      },
      blur: {
        true: 'backdrop-blur-xs',
        false: 'backdrop-blur-none',
      },
    },
    defaultVariants: {
      opacity: 'medium',
    },
  },
)

export type DialogOverlayProps = React.ComponentPropsWithRef<
  typeof DialogPrimitive.Overlay
> &
  VariantProps<typeof dialogOverlayVariants>

const DialogOverlay = ({
  opacity,
  blur,
  className,
  ...props
}: DialogOverlayProps) => {
  return (
    <DialogPrimitive.Overlay
      data-testid="dialog-overlay"
      className={cn(dialogOverlayVariants({ opacity, blur }), className)}
      {...props}
    ></DialogPrimitive.Overlay>
  )
}

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('px-6 py-4 pt-6', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

const DialogBody = ({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('mr-2 flex-1 overflow-y-auto p-6 pr-4 pt-0', className)}
    {...props}
  >
    {children}
  </div>
)
DialogBody.displayName = 'DialogBody'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-testid="dialog-footer"
    className={cn(
      'flex flex-col-reverse p-6 pb-0 sm:flex-row sm:justify-end sm:space-x-2',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof DialogPrimitive.Title>) => (
  <DialogPrimitive.Title
    className={cn('text-md font-bold leading-[120%] tracking-tight', className)}
    {...props}
  />
)
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof DialogPrimitive.Description>) => (
  <DialogPrimitive.Description
    className={cn('text-xs text-gray-500', className)}
    {...props}
  />
)
DialogDescription.displayName = DialogPrimitive.Description.displayName

const dialogContentVariants = cva(
  'max-h-[80vh] pb-6 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-bottom-2 data-[state=open]:slide-in-from-bottom-2 fixed top-1/2 left-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2 bg-white shadow-lg duration-200 sm:rounded-2xl',
  {
    variants: {
      size: {
        sm: 'max-w-(--container-sm)',
        md: 'max-w-(--container-sm) md:max-w-(--container-md)',
        lg: 'max-w-(--container-sm) md:max-w-(--container-md) lg:max-w-(--container-lg)',
        xl: 'max-w-(--container-sm) md:max-w-(--container-md) lg:max-w-(--container-lg) xl:max-w-(--container-xl)',
        '2xl':
          'max-w-(--container-sm) md:max-w-(--container-md) lg:max-w-(--container-lg) xl:max-w-(--container-xl) 2xl:max-w-(--container-2xl)',
      },
    },
    defaultVariants: {
      size: 'lg',
    },
  },
)

type DialogContentProps = React.ComponentPropsWithRef<
  typeof DialogPrimitive.Content
> &
  VariantProps<typeof dialogContentVariants> & {
    overlayProps?: DialogOverlayProps
    /**
     * Accessible name for the close button (e.g. "Close"). Required — provide
     * a localized string from the consuming app.
     */
    closeAriaLabel: string
  }

const DialogContent = ({
  className,
  children,
  size,
  overlayProps,
  closeAriaLabel,
  ...props
}: DialogContentProps) => {
  return (
    <DialogPortal>
      <DialogOverlay {...overlayProps} />
      <DialogPrimitive.Content
        className={cn(dialogContentVariants({ size }), className)}
        {...props}
      >
        {children}
        <DialogClose closeAriaLabel={closeAriaLabel} />
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

type DialogCloseProps = Omit<
  React.ComponentProps<typeof DialogPrimitiveClose>,
  'aria-label'
> & {
  /**
   * Accessible name for the close button (e.g. "Close"). Required — provide
   * a localized string from the consuming app.
   */
  closeAriaLabel: string
}

const DialogClose = ({
  className,
  closeAriaLabel,
  ...props
}: DialogCloseProps) => {
  warnIfEmptyLabel(closeAriaLabel, 'DialogClose: closeAriaLabel')
  return (
    <DialogPrimitiveClose
      className={cn(
        'absolute right-4 top-4 inline-flex size-9 cursor-pointer items-center justify-center rounded-full text-black transition-opacity hover:bg-gray-100 hover:opacity-100 focus:bg-gray-200 focus:text-gray-500 disabled:pointer-events-none',
        className,
      )}
      {...props}
    >
      <Close className="size-4.5" />
      <span className="sr-only">{closeAriaLabel}</span>
    </DialogPrimitiveClose>
  )
}

export {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
}
